import React, { useMemo } from "react";
import TemperatureGraphs from "./TemperatureGraphs";
import getCoachImage from "../../utils/coachImageHelper.js";

const LOCO_TYPES = new Set(["LOCO", "WAG12", "WAP5"]);

// Helper function for safe toFixed
const safeFixed = (val, digits = 1) =>
  typeof val === "number" ? val.toFixed(digits) : "--";

const SelectedCoachSlider = ({
  selectedCoachIndex,
  allCoaches,
  groupedTemperatureData,
  defaultCoachType,
}) => {
  const selectedCoach = useMemo(() => {
    if (!allCoaches.length) return null;
    const normalizedIndex = selectedCoachIndex % allCoaches.length;
    return allCoaches[normalizedIndex];
  }, [selectedCoachIndex, allCoaches]);

  const nextCoach = useMemo(() => {
    if (!allCoaches.length) return null;
    const nextIndex = (selectedCoachIndex + 1) % allCoaches.length;
    return allCoaches[nextIndex];
  }, [selectedCoachIndex, allCoaches]);

  const tempLabels = ["AL", "AR", "WL", "WR", "BL", "BR"];

  const renderCoach = (coach, isSelected = false) => {
    if (!coach) return null;

    const axleSectionBorder = isSelected
      ? "border-4 border-blue-900 p-4"
      : "border border-gray-400 p-4";

    const renderAxle = (axle) => {
      const ts1 = axle.temperatures[0] || {};
      const ts2 = axle.temperatures[1] || {};
      const ts3 = axle.temperatures[2] || {};
      const ts4 = axle.temperatures[3] || {};

      const upperAxleColor = ts1.hasAlarm
        ? "bg-red-500"
        : ts1.hasWarning
        ? "bg-yellow-500"
        : "bg-gray-800";
      const lowerAxleColor = ts2.hasAlarm
        ? "bg-red-500"
        : ts2.hasWarning
        ? "bg-yellow-500"
        : "bg-gray-800";
      const upperWheelColor = ts3.hasAlarm
        ? "bg-red-500"
        : ts3.hasWarning
        ? "bg-yellow-500"
        : "bg-gray-600";
      const lowerWheelColor = ts4.hasAlarm
        ? "bg-red-500"
        : ts4.hasWarning
        ? "bg-yellow-500"
        : "bg-gray-600";

      return (
        <div key={axle.axleNo} className="flex flex-col items-center">
          <div className="flex flex-col items-start text-[10px] mb-2 min-w-[60px]">
            {[0, 2].map(
              (idx) =>
                axle.temperatures[idx] && (
                  <span
                    key={idx}
                    className={`font-bold ${
                      axle.temperatures[idx].hasAlarm
                        ? "text-red-500"
                        : axle.temperatures[idx].hasWarning
                        ? "text-yellow-500"
                        : "text-gray-700"
                    }`}
                  >
                    {tempLabels[idx]} -{" "}
                    {safeFixed(axle.temperatures[idx].value)}°
                  </span>
                )
            )}
          </div>
          <div className="flex flex-col items-center">
            <div className="w-[0.3rem] h-[0.2rem] bg-gray-500" />
            <div className={`w-3 h-2.5 ${upperAxleColor}`} />
            <div className="w-[0.3rem] h-1 bg-gray-600" />
            <div className={`w-6 h-1 ${upperWheelColor}`} />
            <div className={`w-8 h-2 ${upperWheelColor}`} />
            <div className="flex flex-col items-center">
              <div className="w-[0.3rem] h-0.5 bg-gray-600" />
              <div className="w-[1rem] h-1 bg-gray-600" />
              <div className="w-[0.3rem] h-1.5 bg-gray-600" />
              <div className="w-[0.3rem] h-1.5 bg-gray-600" />
              <div className="w-[1rem] h-1 bg-gray-600" />
              <div className="w-[0.3rem] h-0.5 bg-gray-600" />
            </div>
            <div className={`w-8 h-2 ${lowerWheelColor}`} />
            <div className={`w-6 h-1 ${lowerWheelColor}`} />
            <div className="w-[0.3rem] h-1 bg-gray-600" />
            <div className={`w-3 h-2.5 ${lowerAxleColor}`} />
            <div className="w-[0.3rem] h-[0.2rem] bg-gray-500" />
          </div>
          <div className="flex flex-col items-start text-[10px] mt-2 min-w-[60px]">
            {[1, 3].map(
              (idx) =>
                axle.temperatures[idx] && (
                  <span
                    key={idx}
                    className={`font-bold ${
                      axle.temperatures[idx].hasAlarm
                        ? "text-red-500"
                        : axle.temperatures[idx].hasWarning
                        ? "text-yellow-500"
                        : "text-gray-700"
                    }`}
                  >
                    {tempLabels[idx]} -{" "}
                    {safeFixed(axle.temperatures[idx].value)}°
                  </span>
                )
            )}
          </div>
        </div>
      );
    };

    const isLocoOrWag12 = LOCO_TYPES.has(coach.position);
    const axlesToDisplay = isLocoOrWag12 ? coach.axles.slice(0, 6) : coach.axles;

    const hasFourAxles = axlesToDisplay.length === 4;
    const firstTwo = axlesToDisplay.slice(0, 2);
    const lastTwo = axlesToDisplay.slice(2, 4);

    const coachInfo =
      LOCO_TYPES.has(coach.position) || !coach.coachNumber
        ? coach.label
        : `${coach.label} | ${coach.coachNumber}`;

    const hasAverages =
      coach.averages &&
      typeof coach.averages.LHSideAxleAvg === "number" &&
      typeof coach.averages.RHSideAxleAvg === "number" &&
      typeof coach.averages.WholeCoachAxleAvg === "number";

    return (
      <div className="flex flex-col items-center gap-4 w-full p-2">
        {/* Image + Label */}
        <div className="w-full">
          <div className="relative w-full h-40">
            <img
              src={getCoachImage(coach, defaultCoachType)}
              alt={coach.label}
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 flex justify-center items-center">
              <div className="bg-black bg-opacity-60 w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center">
                <span className="text-white font-extrabold text-center leading-none text-base md:text-lg">
                  {coach.label}
                </span>
              </div>
            </div>
          </div>
          <div className="text-center text-black text-lg font-bold -mt-2">
            {coachInfo}
          </div>
        </div>

        {/* Axle Section */}
        <div className={`mt-2 ${axleSectionBorder}`}>
          {isLocoOrWag12 ? (
            <div className="flex justify-center">
              {axlesToDisplay.map(renderAxle)}
            </div>
          ) : hasFourAxles ? (
            <div
              className={`flex flex-wrap justify-center ${
                hasAverages ? "gap-4" : "gap-14"
              }`}
            >
              <div className="flex">{firstTwo.map(renderAxle)}</div>

              {/* Averages */}
              <div className="flex flex-col items-center gap-y-6 text-[10px] font-bold text-gray-700">
                {hasAverages && (
                  <>
                    <div className="flex flex-col items-center">
                      <span>LH Axle Avg</span>
                      <span>{safeFixed(coach.averages.LHSideAxleAvg)}°</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span>RH Axle Avg</span>
                      <span>{safeFixed(coach.averages.RHSideAxleAvg)}°</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span>Coach Axle Avg</span>
                      <span>{safeFixed(coach.averages.WholeCoachAxleAvg)}°</span>
                    </div>
                  </>
                )}
              </div>

              <div className="flex">{lastTwo.map(renderAxle)}</div>
            </div>
          ) : (
            <div className="flex gap-8 justify-center">
              {axlesToDisplay.map(renderAxle)}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!selectedCoach) return null;

  return (
    <div className="relative w-full max-w-6xl mx-auto bg-white p-4 mt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>{renderCoach(selectedCoach, true)}</div>
        <div>{renderCoach(nextCoach)}</div>
      </div>

      <div className="text-center font-bold text-lg mt-2">
        Detailed Coach View
      </div>

      <div className="w-full">
        {groupedTemperatureData && (
          <TemperatureGraphs apiData={groupedTemperatureData} />
        )}
      </div>
    </div>
  );
};

export default SelectedCoachSlider;
